import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Crown, Headset, Newspaper, PlusCircle } from '@liveauctioneers/hammer-ui-core/icons';
import { Modal } from '@liveauctioneers/hammer-ui-core/modal';
import {
    CloseButton,
    ModalActions,
    ModalHeader,
    ModalSubTitle,
} from '@liveauctioneers/hammer-ui-core/modal/components';
import { BodySecondary, H2, H5 } from '@liveauctioneers/hammer-ui-core/text';
import { ComponentProps, ComponentType, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getUserFirstName } from '@/redux/modules/account/user/user.selectors';
import { dismissModal } from '@/redux/modules/modal';
import { TranslationBlock, useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { BenefitRow } from './components/BenefitRow';

const PROFILE_PATH = '/dashboard/profile';

type Benefit = {
    icon: ComponentType<Pick<ComponentProps<typeof Headset>, 'color' | 'size'>>;
    label: keyof TranslationBlock['vipConfirmationModal']['benefits'];
};

const BENEFITS: Benefit[] = [
    { icon: Headset, label: 'prioritySupport' },
    { icon: Crown, label: 'vipStatus' },
    { icon: Newspaper, label: 'atgSubscription' },
    { icon: PlusCircle, label: 'andMore' },
];

export const VipConfirmationModal = () => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const firstName = useAppSelector(getUserFirstName);
    const navigate = useNavigate();
    const [show, setShow] = useState(true);
    const shouldNavigateOnCloseRef = useRef(false);

    const handleClose = useCallback(() => {
        setShow(false);
    }, []);

    const handleCloseAnimationEnd = useCallback(() => {
        dispatch(dismissModal());
        if (shouldNavigateOnCloseRef.current) {
            navigate(PROFILE_PATH);
        }
    }, [dispatch, navigate]);

    const handleLearnMore = useCallback(() => {
        shouldNavigateOnCloseRef.current = true;
        setShow(false);
    }, []);

    return (
        <Modal
            handleClose={handleClose}
            onModalCloseAnimationEnd={handleCloseAnimationEnd}
            show={show}
            type="composable"
        >
            <CloseButton />
            <ModalHeader>
                <Crown
                    className="mb-xs"
                    color="warning"
                    size="lg"
                    style="duotone"
                />
                <H2>
                    {formatMessage(['vipConfirmationModal', 'title'], {
                        values: { firstName: firstName ? `, ${firstName}` : '' },
                    })}
                </H2>
                <ModalSubTitle className="mb-xs">{formatMessage(['vipConfirmationModal', 'subtitle'])}</ModalSubTitle>
            </ModalHeader>
            <div className="px-md pt-xs smMin:px-2xl">
                <div className="flex w-full flex-col gap-md">
                    <H5>{formatMessage(['vipConfirmationModal', 'benefitHeading'])}</H5>
                    <div className="flex w-full flex-col gap-xs">
                        {BENEFITS.map((benefit) => (
                            <BenefitRow
                                key={benefit.label}
                                icon={
                                    <benefit.icon
                                        color="secondary"
                                        size="md"
                                    />
                                }
                            >
                                {formatMessage(['vipConfirmationModal', 'benefits', benefit.label])}
                            </BenefitRow>
                        ))}
                        <div className="w-full border-t border-border-secondary" />
                        <BodySecondary color="secondary">
                            {formatMessage(['vipConfirmationModal', 'accountSettingsNote'])}
                        </BodySecondary>
                    </div>
                </div>
            </div>
            <ModalActions>
                <div className="flex justify-center">
                    <Button onClick={handleLearnMore}>{formatMessage(['vipConfirmationModal', 'cta'])}</Button>
                </div>
            </ModalActions>
        </Modal>
    );
};
